import {
  deleteField,
  type DocumentReference,
  type Transaction,
} from 'firebase/firestore';
import type {
  ExpenseEntryData,
  InvoiceAllocationCoordinatorData,
  InvoiceNumberCounterData,
  InvoicePresentationSnapshotData,
  InvoiceTimelineEventData,
  InvoiceTimelineUserActor,
  MatterData,
  MatterDraftInvoiceState,
  TimeEntryData,
  TransactionData,
  V3InvoiceData,
  V3InvoicePresentationSnapshotData,
} from '@shared/types';
import { cleanDataToFirestore } from './converters';

export interface VoidFinalizedInvoiceProps {
  invoiceId: string;
  timeEntryIds?: string[];
  expenseEntryIds?: string[];
  actor: InvoiceTimelineUserActor;
  voidReason?: string;
}
export interface VoidedInvoiceSourceFingerprint {
  entryType: 'timeEntry' | 'expenseEntry';
  entryId: string;
  fingerprint: string;
}
export type VoidFinalizedInvoiceResult =
  | {
      status: 'voided';
      releasedTimeEntryCount: number;
      releasedExpenseEntryCount: number;
      releasedSourceFingerprints: VoidedInvoiceSourceFingerprint[];
      voidedAt: Date;
    }
  | { status: 'missing' }
  | { status: 'allocation-aware' }
  | { status: 'not-finalized'; invoiceStatus: string | undefined };
export interface RestoreVoidedInvoiceProps {
  invoiceId: string;
  actor: InvoiceTimelineUserActor;
  expectedVoidedAt: Date;
  expectedSourceFingerprints: VoidedInvoiceSourceFingerprint[];
}
export type RestoreVoidedInvoiceResult =
  | {
      status: 'restored';
      restoredTimeEntryCount: number;
      restoredExpenseEntryCount: number;
    }
  | { status: 'missing' }
  | { status: 'not-voided'; invoiceStatus: string | undefined }
  | { status: 'stale-void' }
  | { status: 'conflict'; reason: 'allocation-changed' }
  | {
      status: 'conflict';
      reason: 'missing-source' | 'cross-matter' | 'assigned' | 'changed';
      entryType: 'timeEntry' | 'expenseEntry';
      entryId: string;
      currentInvoiceId?: string;
    };
export interface RevertFinalizedInvoiceToDraftProps {
  invoiceId: string;
  actor: InvoiceTimelineUserActor;
  expectedFinalizedTimestamp: Date;
  presentationSnapshotId: string;
  retainerTransferId?: string;
  implicitDraft: {
    matterId: string;
    timeEntryIds: string[];
    expenseEntryIds: string[];
    originalDraftState?: MatterDraftInvoiceState;
  };
}
export type RevertFinalizedInvoiceToDraftResult =
  | { status: 'reverted' }
  | { status: 'missing' }
  | { status: 'not-finalized'; invoiceStatus: string | undefined }
  | { status: 'stale-finalization' }
  | {
      status: 'conflict';
      reason: 'draft-state-changed' | 'allocation-changed';
    }
  | {
      status: 'conflict';
      reason: 'missing-source' | 'cross-matter' | 'not-linked';
      entryType: 'timeEntry' | 'expenseEntry';
      entryId: string;
      currentInvoiceId?: string;
    };
export type RemoveDraftInvoiceSourceEntryResult =
  | {
      status: 'removed';
      entryType: 'timeEntry' | 'expenseEntry';
      entryId: string;
      markedUnbillable: boolean;
    }
  | { status: 'missing-invoice' }
  | { status: 'not-draft'; invoiceStatus: string | undefined }
  | {
      status: 'conflict';
      reason: 'missing-source' | 'cross-matter' | 'not-linked';
      entryType: 'timeEntry' | 'expenseEntry';
      entryId: string;
      currentInvoiceId?: string;
    };
export type AttachDraftInvoiceSourceEntriesResult =
  | {
      status: 'attached';
      attachedTimeEntryCount: number;
      attachedExpenseEntryCount: number;
    }
  | { status: 'missing-invoice' }
  | { status: 'not-draft'; invoiceStatus: string | undefined }
  | {
      status: 'conflict';
      reason:
        | 'missing-source'
        | 'assigned'
        | 'unbillable'
        | 'cross-matter'
        | 'draft-time-entry';
      entryType: 'timeEntry' | 'expenseEntry';
      entryId: string;
      currentInvoiceId?: string;
    };
export type ApproveDraftInvoiceFinalizedFields = Pick<
  V3InvoiceData,
  | 'amount'
  | 'manualAdjustments'
  | 'outstandingBalance'
  | 'previousInvoice'
  | 'retainerFundsApplied'
  | 'retainerFundsPriorToInvoice'
  | 'retainerFundsRemaining'
  | 'startYmd'
> & {
  endYmd: NonNullable<V3InvoiceData['endYmd']>;
  expenseEntries: NonNullable<V3InvoiceData['expenseEntries']>;
  timeEntries: NonNullable<V3InvoiceData['timeEntries']>;
  url: string;
};
export type ApproveDraftInvoicePresentationSnapshotData = Omit<
  V3InvoicePresentationSnapshotData,
  'createdTimestamp'
> & { createdTimestamp?: Date };
export interface ApproveDraftInvoiceProps {
  invoiceId: string;
  actor: InvoiceTimelineUserActor;
  timeEntryIds?: string[];
  expenseEntryIds?: string[];
  finalizedFields: ApproveDraftInvoiceFinalizedFields;
  presentationSnapshotData: ApproveDraftInvoicePresentationSnapshotData;
  retainerTransferId?: string;
}
type AttachDraftInvoiceConflictReason = Extract<
  AttachDraftInvoiceSourceEntriesResult,
  { status: 'conflict' }
>['reason'];
type FinalizeImplicitMatterDraftConflictReason =
  | 'missing-source'
  | 'cross-matter'
  | 'assigned'
  | 'unbillable'
  | 'draft-time-entry'
  | 'removed-source';
export interface DraftInvoiceSourceRef {
  entryType: 'timeEntry' | 'expenseEntry';
  entryId: string;
  ref: DocumentReference<TimeEntryData | ExpenseEntryData>;
}

export interface InvoiceTimelineEventWrite {
  actor: InvoiceTimelineUserActor;
  eventRef: DocumentReference<InvoiceTimelineEventData>;
  occurredTimestamp: Date;
}

const writeInvoiceTimelineEvent = (
  transaction: Transaction,
  write: InvoiceTimelineEventWrite,
  event: Pick<
    InvoiceTimelineEventData,
    'invoiceId' | 'matterId' | 'type' | 'details'
  >,
) =>
  transaction.set(
    write.eventRef,
    cleanDataToFirestore({
      schemaVersion: 1,
      operationId: write.eventRef.id,
      actor: write.actor,
      occurredTimestamp: write.occurredTimestamp,
      ...event,
    }),
  );

export const deriveNextInvoiceNumberFromInvoices = (
  invoices: { invoiceNumber?: unknown }[],
): number =>
  invoices.reduce((nextInvoiceNumber, invoice) => {
    const invoiceNumber =
      typeof invoice.invoiceNumber === 'number'
        ? invoice.invoiceNumber
        : typeof invoice.invoiceNumber === 'string'
          ? Number(invoice.invoiceNumber)
          : undefined;
    return invoiceNumber !== undefined &&
      Number.isFinite(invoiceNumber) &&
      invoiceNumber >= 0
      ? Math.max(nextInvoiceNumber, Math.floor(invoiceNumber) + 1)
      : nextInvoiceNumber;
  }, 1);

const sourceRefKey = (ref: {
  entryType: 'timeEntry' | 'expenseEntry';
  entryId: string;
}) => `${ref.entryType}:${ref.entryId}`;
const sourceIdsMatch = (expectedIds: string[], actualIds: string[]) => {
  if (expectedIds.length !== actualIds.length) return false;
  const actual = new Set(actualIds);
  return expectedIds.every((id) => actual.has(id));
};
const finalizeConflict = (
  sourceRef: DraftInvoiceSourceRef,
  reason: FinalizeImplicitMatterDraftConflictReason,
  currentInvoiceId?: string,
) =>
  ({
    status: 'conflict',
    reason,
    entryType: sourceRef.entryType,
    entryId: sourceRef.entryId,
    ...(currentInvoiceId !== undefined ? { currentInvoiceId } : {}),
  }) as const;

const getFinalizeImplicitMatterDraftConflict = (
  sourceRef: DraftInvoiceSourceRef,
  data: TimeEntryData | ExpenseEntryData | undefined,
  matterId: string,
  removedKeys: Set<string>,
): ReturnType<typeof finalizeConflict> | undefined =>
  removedKeys.has(sourceRefKey(sourceRef))
    ? finalizeConflict(sourceRef, 'removed-source')
    : !data
      ? finalizeConflict(sourceRef, 'missing-source')
      : data.matter !== matterId
        ? finalizeConflict(sourceRef, 'cross-matter')
        : data.invoiceId !== undefined
          ? finalizeConflict(sourceRef, 'assigned', data.invoiceId)
          : data.billingState === 'unbillable'
            ? finalizeConflict(sourceRef, 'unbillable')
            : sourceRef.entryType === 'timeEntry' &&
                (data as TimeEntryData).status === 'draft'
              ? finalizeConflict(sourceRef, 'draft-time-entry')
              : undefined;

export const finalizeImplicitMatterDraftInTransaction = async (props: {
  transaction: Transaction;
  firmId: string;
  matterId: string;
  matterRef: DocumentReference<MatterData>;
  draftStateRef: DocumentReference<MatterDraftInvoiceState>;
  invoiceRef: DocumentReference<V3InvoiceData>;
  counterRef: DocumentReference<InvoiceNumberCounterData>;
  invoiceAllocationCoordinatorRef: DocumentReference<InvoiceAllocationCoordinatorData>;
  presentationSnapshotRef: DocumentReference<V3InvoicePresentationSnapshotData>;
  retainerTransferRef?: DocumentReference<TransactionData>;
  sourceRefs: DraftInvoiceSourceRef[];
  initialNextInvoiceNumber: number;
  expectedInvoiceNumber?: number;
  expectedDraftStateUpdatedTimestamp?: Date;
  finalizedFields: ApproveDraftInvoiceFinalizedFields;
  presentationSnapshotData: ApproveDraftInvoicePresentationSnapshotData;
  timelineEvent: InvoiceTimelineEventWrite;
}) => {
  const [
    matter,
    draftState,
    counter,
    retainerTransferSnapshot,
    invoiceAllocationCoordinatorSnapshot,
  ] = await Promise.all([
    props.transaction.get(props.matterRef),
    props.transaction.get(props.draftStateRef),
    props.transaction.get(props.counterRef),
    props.retainerTransferRef
      ? props.transaction.get(props.retainerTransferRef)
      : undefined,
    props.transaction.get(props.invoiceAllocationCoordinatorRef),
  ]);
  const sourceEntries = await Promise.all(
    props.sourceRefs.map(async (sourceRef) => ({
      sourceRef,
      data: (await props.transaction.get(sourceRef.ref)).data(),
    })),
  );
  const matterData = matter.data();
  if (!matterData) return { status: 'missing-matter' } as const;
  if (matterData.isUninvoiceable === true)
    return { status: 'matter-uninvoiceable' } as const;

  const currentState = draftState.data();
  if (
    currentState?.updatedTimestamp?.getTime() !==
    props.expectedDraftStateUpdatedTimestamp?.getTime()
  )
    return { status: 'stale-draft-state' } as const;
  const manualAdjustments = currentState?.manualAdjustments ?? [];

  const removedKeys = new Set(
    (currentState?.removedSourceRefs ?? []).map(sourceRefKey),
  );
  const includedSourceEntries = sourceEntries.filter(
    ({ sourceRef, data }) =>
      !getFinalizeImplicitMatterDraftConflict(
        sourceRef,
        data,
        props.matterId,
        removedKeys,
      ),
  );
  const expectedTimeEntryIds = props.finalizedFields.timeEntries.map(
    ({ id }) => id,
  );
  const expectedExpenseEntryIds = props.finalizedFields.expenseEntries.map(
    ({ id }) => id,
  );
  const actualTimeEntryIds = includedSourceEntries
    .filter(({ sourceRef }) => sourceRef.entryType === 'timeEntry')
    .map(({ sourceRef }) => sourceRef.entryId);
  const actualExpenseEntryIds = includedSourceEntries
    .filter(({ sourceRef }) => sourceRef.entryType === 'expenseEntry')
    .map(({ sourceRef }) => sourceRef.entryId);
  if (
    !sourceIdsMatch(expectedTimeEntryIds, actualTimeEntryIds) ||
    !sourceIdsMatch(expectedExpenseEntryIds, actualExpenseEntryIds)
  ) {
    const expectedKeys = new Set([
      ...expectedTimeEntryIds.map((entryId) =>
        sourceRefKey({ entryType: 'timeEntry', entryId }),
      ),
      ...expectedExpenseEntryIds.map((entryId) =>
        sourceRefKey({ entryType: 'expenseEntry', entryId }),
      ),
    ]);
    for (const { sourceRef, data } of sourceEntries) {
      if (!expectedKeys.has(sourceRefKey(sourceRef))) continue;
      const conflict = getFinalizeImplicitMatterDraftConflict(
        sourceRef,
        data,
        props.matterId,
        removedKeys,
      );
      if (conflict) return conflict;
    }
    return { status: 'stale-source-projection' } as const;
  }
  if (includedSourceEntries.length === 0 && manualAdjustments.length === 0)
    return { status: 'nothing-to-invoice' } as const;
  const retainerFundsApplied = props.finalizedFields.retainerFundsApplied ?? 0;
  const retainerTransfer = retainerTransferSnapshot?.data();
  if (
    retainerFundsApplied > 0 &&
    (!retainerTransfer ||
      !isActiveRetainerTransfer({
        matterId: props.matterId,
        retainerFundsApplied,
        retainerTransfer,
      }))
  )
    return { status: 'missing-retainer-transfer' } as const;

  const counterNext = counter.data()?.nextInvoiceNumber;
  const invoiceNumber = Math.max(
    props.initialNextInvoiceNumber,
    typeof counterNext === 'number' && Number.isFinite(counterNext)
      ? counterNext
      : 1,
  );
  if (
    props.expectedInvoiceNumber !== undefined &&
    props.expectedInvoiceNumber !== invoiceNumber
  )
    return { status: 'stale-invoice-number', invoiceNumber } as const;
  const invoiceAllocationCoordinatorRevision = coordinatorMutationRevision(
    invoiceAllocationCoordinatorSnapshot.data(),
  );
  const finalizedTimestamp = props.timelineEvent.occurredTimestamp;
  props.transaction.set(
    props.invoiceRef,
    cleanDataToFirestore({
      ...props.finalizedFields,
      createdTimestamp: finalizedTimestamp,
      finalizedTimestamp,
      firmId: props.firmId,
      ...(invoiceAllocationCoordinatorRevision !== undefined
        ? { invoiceAllocationCoordinatorRevision }
        : {}),
      invoiceNumber: String(invoiceNumber),
      manualAdjustments,
      matterId: props.matterId,
      ...(props.retainerTransferRef
        ? { retainerTransferId: props.retainerTransferRef.id }
        : {}),
      schemaVersion: 3,
      status: 'finalized',
    } as V3InvoiceData),
  );
  if (props.retainerTransferRef) {
    props.transaction.update(props.retainerTransferRef, {
      finalizedInvoiceId: props.invoiceRef.id,
    });
  }
  props.transaction.set(
    props.counterRef,
    { nextInvoiceNumber: invoiceNumber + 1 },
    { merge: true },
  );
  for (const { sourceRef } of includedSourceEntries) {
    props.transaction.update(sourceRef.ref, { invoiceId: props.invoiceRef.id });
  }
  props.transaction.delete(props.draftStateRef);
  props.transaction.set(
    props.presentationSnapshotRef,
    cleanDataToFirestore({
      ...props.presentationSnapshotData,
      createdTimestamp:
        props.presentationSnapshotData.createdTimestamp ?? finalizedTimestamp,
    }),
  );
  writeInvoiceTimelineEvent(props.transaction, props.timelineEvent, {
    invoiceId: props.invoiceRef.id,
    matterId: props.matterId,
    type: 'invoice.finalized',
    details: {
      invoiceNumber: String(invoiceNumber),
      presentationSnapshotId: props.presentationSnapshotRef.id,
      frozenSourceCounts: {
        timeEntries: props.finalizedFields.timeEntries.length,
        expenseEntries: props.finalizedFields.expenseEntries.length,
      },
      ...(retainerFundsApplied > 0
        ? { retainerAmount: retainerFundsApplied }
        : {}),
    },
  });
  return {
    status: 'approved',
    finalizedTimestamp,
    invoiceId: props.invoiceRef.id,
    invoiceNumber: String(invoiceNumber),
    presentationSnapshotId: props.presentationSnapshotRef.id,
    ...(props.retainerTransferRef
      ? { retainerTransferId: props.retainerTransferRef.id }
      : {}),
  } as const;
};

export type FinalizeImplicitMatterDraftResult = Awaited<
  ReturnType<typeof finalizeImplicitMatterDraftInTransaction>
>;

const isActiveRetainerTransfer = (props: {
  matterId: string;
  retainerFundsApplied: number;
  retainerTransfer: TransactionData;
}) =>
  (props.retainerTransfer.deletedAt === undefined ||
    props.retainerTransfer.deletedAt === null) &&
  (props.retainerTransfer.deletionReason === undefined ||
    props.retainerTransfer.deletionReason === null) &&
  props.retainerTransfer.deletedBySource !== true &&
  (props.retainerTransfer.finalizedInvoiceId === undefined ||
    props.retainerTransfer.finalizedInvoiceId === null) &&
  props.retainerTransfer.type === 'retainerTransfer' &&
  props.retainerTransfer.matter === props.matterId &&
  props.retainerTransfer.amount === props.retainerFundsApplied &&
  props.retainerTransfer.status === 'draft';

export const voidFinalizedInvoiceInTransaction = async (props: {
  transaction: Transaction;
  invoiceRef: DocumentReference<V3InvoiceData>;
  invoiceAllocationCoordinatorRef: DocumentReference<InvoiceAllocationCoordinatorData>;
  sourceRefs: DraftInvoiceSourceRef[];
  timelineEvent: InvoiceTimelineEventWrite;
  voidReason?: string;
}): Promise<VoidFinalizedInvoiceResult> => {
  const [invoiceSnapshot, invoiceAllocationCoordinatorSnapshot] =
    await Promise.all([
      props.transaction.get(props.invoiceRef),
      props.transaction.get(props.invoiceAllocationCoordinatorRef),
    ]);
  const invoice = invoiceSnapshot.data();
  if (!invoice) return { status: 'missing' };
  if (invoice.status !== 'finalized') {
    return { status: 'not-finalized', invoiceStatus: invoice.status };
  }
  const sourceEntries = await Promise.all(
    props.sourceRefs.map(async (sourceRef) => ({
      sourceRef,
      data: (await props.transaction.get(sourceRef.ref)).data(),
    })),
  );
  const voidedAt = props.timelineEvent.occurredTimestamp;
  const voidedInvoiceAllocationCoordinatorRevision =
    coordinatorMutationRevision(invoiceAllocationCoordinatorSnapshot.data());
  let releasedTimeEntryCount = 0;
  let releasedExpenseEntryCount = 0;
  const releasedSourceFingerprints: VoidedInvoiceSourceFingerprint[] = [];
  props.transaction.update(
    props.invoiceRef,
    cleanDataToFirestore({
      status: 'voided',
      voidedAt,
      voidedBy: props.timelineEvent.actor.userId,
      ...(voidedInvoiceAllocationCoordinatorRevision !== undefined
        ? { voidedInvoiceAllocationCoordinatorRevision }
        : {}),
      ...(props.voidReason ? { voidReason: props.voidReason } : {}),
    } satisfies Partial<V3InvoiceData>),
  );
  for (const { sourceRef, data } of sourceEntries) {
    // Only release entries still owned by this invoice; anything else already moved.
    if (data?.invoiceId !== props.invoiceRef.id) continue;
    releasedSourceFingerprints.push({
      entryType: sourceRef.entryType,
      entryId: sourceRef.entryId,
      fingerprint: await sourceEntryFingerprint(data),
    });
    props.transaction.update(sourceRef.ref, { invoiceId: deleteField() });
    if (sourceRef.entryType === 'timeEntry') releasedTimeEntryCount += 1;
    else releasedExpenseEntryCount += 1;
  }
  writeInvoiceTimelineEvent(props.transaction, props.timelineEvent, {
    invoiceId: props.invoiceRef.id,
    matterId: invoice.matterId,
    type: 'invoice.voided',
    details: {
      ...(typeof invoice.invoiceNumber === 'string'
        ? { invoiceNumber: invoice.invoiceNumber }
        : {}),
      ...(props.voidReason ? { voidReason: props.voidReason } : {}),
      releasedSourceCounts: {
        timeEntries: releasedTimeEntryCount,
        expenseEntries: releasedExpenseEntryCount,
      },
    },
  });
  return {
    status: 'voided',
    releasedTimeEntryCount,
    releasedExpenseEntryCount,
    releasedSourceFingerprints,
    voidedAt,
  };
};

export const restoreVoidedInvoiceInTransaction = async (props: {
  transaction: Transaction;
  invoiceRef: DocumentReference<V3InvoiceData>;
  invoiceAllocationCoordinatorRef: DocumentReference<InvoiceAllocationCoordinatorData>;
  sourceRefs: DraftInvoiceSourceRef[];
  expectedVoidedAt: Date;
  expectedSourceFingerprints: VoidedInvoiceSourceFingerprint[];
  timelineEvent: InvoiceTimelineEventWrite;
}): Promise<RestoreVoidedInvoiceResult> => {
  const [invoiceSnapshot, invoiceAllocationCoordinatorSnapshot] =
    await Promise.all([
      props.transaction.get(props.invoiceRef),
      props.transaction.get(props.invoiceAllocationCoordinatorRef),
    ]);
  const invoice = invoiceSnapshot.data();
  if (!invoice) return { status: 'missing' };
  if (invoice.status !== 'voided') {
    return { status: 'not-voided', invoiceStatus: invoice.status };
  }
  if (invoice.voidedAt?.getTime() !== props.expectedVoidedAt.getTime()) {
    return { status: 'stale-void' };
  }
  if (
    !hasMatchingCoordinatorRevision({
      current: invoiceAllocationCoordinatorSnapshot.data(),
      expected: invoice.voidedInvoiceAllocationCoordinatorRevision,
    })
  ) {
    return { status: 'conflict', reason: 'allocation-changed' };
  }
  const sourceEntries = await Promise.all(
    props.sourceRefs.map(async (sourceRef) => ({
      sourceRef,
      data: (await props.transaction.get(sourceRef.ref)).data(),
    })),
  );
  const expectedFingerprintsBySource = new Map(
    props.expectedSourceFingerprints.map((source) => [
      `${source.entryType}:${source.entryId}`,
      source.fingerprint,
    ]),
  );
  for (const { sourceRef, data } of sourceEntries) {
    if (!data) {
      return {
        status: 'conflict',
        reason: 'missing-source',
        entryType: sourceRef.entryType,
        entryId: sourceRef.entryId,
      };
    }
    if (data.matter !== invoice.matterId) {
      return {
        status: 'conflict',
        reason: 'cross-matter',
        entryType: sourceRef.entryType,
        entryId: sourceRef.entryId,
      };
    }
    if (
      data.invoiceId !== undefined &&
      data.invoiceId !== props.invoiceRef.id
    ) {
      return {
        status: 'conflict',
        reason: 'assigned',
        entryType: sourceRef.entryType,
        entryId: sourceRef.entryId,
        currentInvoiceId: data.invoiceId,
      };
    }
    const expectedFingerprint = expectedFingerprintsBySource.get(
      `${sourceRef.entryType}:${sourceRef.entryId}`,
    );
    if (
      expectedFingerprint === undefined ||
      (await sourceEntryFingerprint(data)) !== expectedFingerprint
    ) {
      return {
        status: 'conflict',
        reason: 'changed',
        entryType: sourceRef.entryType,
        entryId: sourceRef.entryId,
      };
    }
  }
  props.transaction.update(props.invoiceRef, {
    status: 'finalized',
    voidedAt: deleteField(),
    voidedBy: deleteField(),
    voidedInvoiceAllocationCoordinatorRevision: deleteField(),
    voidReason: deleteField(),
  });
  let restoredTimeEntryCount = 0;
  let restoredExpenseEntryCount = 0;
  for (const { sourceRef, data } of sourceEntries) {
    if (data?.invoiceId === props.invoiceRef.id) continue;
    props.transaction.update(sourceRef.ref, {
      invoiceId: props.invoiceRef.id,
    });
    if (sourceRef.entryType === 'timeEntry') restoredTimeEntryCount += 1;
    else restoredExpenseEntryCount += 1;
  }
  writeInvoiceTimelineEvent(props.transaction, props.timelineEvent, {
    invoiceId: props.invoiceRef.id,
    matterId: invoice.matterId,
    type: 'invoice.voidRestored',
    details: {
      ...(typeof invoice.invoiceNumber === 'string'
        ? { invoiceNumber: invoice.invoiceNumber }
        : {}),
      restoredSourceCounts: {
        timeEntries: restoredTimeEntryCount,
        expenseEntries: restoredExpenseEntryCount,
      },
    },
  });
  return {
    status: 'restored',
    restoredTimeEntryCount,
    restoredExpenseEntryCount,
  };
};

export const revertFinalizedInvoiceToDraftInTransaction = async (props: {
  transaction: Transaction;
  invoiceRef: DocumentReference<V3InvoiceData>;
  invoiceAllocationCoordinatorRef: DocumentReference<InvoiceAllocationCoordinatorData>;
  presentationSnapshotRef: DocumentReference<InvoicePresentationSnapshotData>;
  retainerTransferRef?: DocumentReference<TransactionData>;
  expectedFinalizedTimestamp: Date;
  timelineEvent: InvoiceTimelineEventWrite;
  implicitDraft: {
    matterId: string;
    draftStateRef: DocumentReference<MatterDraftInvoiceState>;
    sourceRefs: DraftInvoiceSourceRef[];
    originalDraftState?: MatterDraftInvoiceState;
  };
}): Promise<RevertFinalizedInvoiceToDraftResult> => {
  const [invoiceSnapshot, invoiceAllocationCoordinatorSnapshot] =
    await Promise.all([
      props.transaction.get(props.invoiceRef),
      props.transaction.get(props.invoiceAllocationCoordinatorRef),
    ]);
  const invoice = invoiceSnapshot.data();
  if (!invoice) return { status: 'missing' };
  if (invoice.status !== 'finalized') {
    return { status: 'not-finalized', invoiceStatus: invoice.status };
  }
  if (
    invoice.finalizedTimestamp?.getTime() !==
    props.expectedFinalizedTimestamp.getTime()
  ) {
    return { status: 'stale-finalization' };
  }
  if (
    !hasMatchingCoordinatorRevision({
      current: invoiceAllocationCoordinatorSnapshot.data(),
      expected: invoice.invoiceAllocationCoordinatorRevision,
    })
  ) {
    return { status: 'conflict', reason: 'allocation-changed' };
  }

  const [currentDraftState, sourceEntries] = await Promise.all([
    props.transaction.get(props.implicitDraft.draftStateRef),
    Promise.all(
      props.implicitDraft.sourceRefs.map(async (sourceRef) => ({
        sourceRef,
        data: (await props.transaction.get(sourceRef.ref)).data(),
      })),
    ),
  ]);
  if (currentDraftState.data() !== undefined) {
    return { status: 'conflict', reason: 'draft-state-changed' };
  }
  for (const { sourceRef, data } of sourceEntries) {
    if (!data) {
      return {
        status: 'conflict',
        reason: 'missing-source',
        entryType: sourceRef.entryType,
        entryId: sourceRef.entryId,
      };
    }
    if (data.matter !== props.implicitDraft.matterId) {
      return {
        status: 'conflict',
        reason: 'cross-matter',
        entryType: sourceRef.entryType,
        entryId: sourceRef.entryId,
      };
    }
    if (data.invoiceId !== props.invoiceRef.id) {
      return {
        status: 'conflict',
        reason: 'not-linked',
        entryType: sourceRef.entryType,
        entryId: sourceRef.entryId,
        currentInvoiceId: data.invoiceId,
      };
    }
  }
  for (const { sourceRef } of sourceEntries) {
    props.transaction.update(sourceRef.ref, { invoiceId: deleteField() });
  }
  if (props.implicitDraft.originalDraftState) {
    props.transaction.set(
      props.implicitDraft.draftStateRef,
      cleanDataToFirestore(props.implicitDraft.originalDraftState),
    );
  }
  props.transaction.delete(props.invoiceRef);

  props.transaction.delete(props.presentationSnapshotRef);
  if (props.retainerTransferRef) {
    props.transaction.delete(props.retainerTransferRef);
  }
  writeInvoiceTimelineEvent(props.transaction, props.timelineEvent, {
    invoiceId: props.invoiceRef.id,
    matterId: invoice.matterId,
    type: 'invoice.finalizationReverted',
    details: {
      ...(typeof invoice.invoiceNumber === 'string'
        ? { invoiceNumber: invoice.invoiceNumber }
        : {}),
      ...(props.implicitDraft
        ? {
            releasedSourceCounts: {
              timeEntries: props.implicitDraft.sourceRefs.filter(
                ({ entryType }) => entryType === 'timeEntry',
              ).length,
              expenseEntries: props.implicitDraft.sourceRefs.filter(
                ({ entryType }) => entryType === 'expenseEntry',
              ).length,
            },
          }
        : {}),
    },
  });
  return { status: 'reverted' };
};

const sourceEntryFingerprint = async (
  data: TimeEntryData | ExpenseEntryData,
): Promise<string> => {
  const { invoiceId: _invoiceId, ...sourceData } = data;
  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(stableSerialize(sourceData)),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
};

const coordinatorMutationRevision = (
  coordinator: InvoiceAllocationCoordinatorData | undefined,
): number | undefined => {
  if (coordinator === undefined) return 0;
  const { mutationRevision } = coordinator;
  return Number.isSafeInteger(mutationRevision) &&
    mutationRevision >= 0 &&
    mutationRevision < Number.MAX_SAFE_INTEGER
    ? mutationRevision
    : undefined;
};

const hasMatchingCoordinatorRevision = (props: {
  current: InvoiceAllocationCoordinatorData | undefined;
  expected: number | undefined;
}): boolean =>
  props.expected !== undefined &&
  props.expected === coordinatorMutationRevision(props.current);

const stableSerialize = (value: unknown): string => {
  if (value instanceof Date) return `date:${value.toISOString()}`;
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(',')}]`;
  }
  if (value !== null && typeof value === 'object') {
    return `{${Object.entries(value)
      .filter(([, entryValue]) => entryValue !== undefined)
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
      .map(
        ([key, entryValue]) =>
          `${JSON.stringify(key)}:${stableSerialize(entryValue)}`,
      )
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
};
